import echarts from 'echarts';
import chroma from 'chroma-js';

import {
  GreyColors,
  tooltipOptions,
  getBins,
} from '../../../shared/chart-options';
import { LineupBarChartTooltip } from '../LineupBarChartTooltip';

const BINS_NUMBER = 7;

export function getLineupBarChartOptions(
  fullGameData,
  shifts,
  teamColor,
  playerSelected,
) {
  const players = getTeamPlayers(shifts);
  const maxValue =
    fullGameData.gameDurationMinutes + fullGameData.overtimeMinutes;

  const chartData = getChartData(shifts, teamColor);
  const lineupBins = getLineupBins(chartData);
  const colorPalette = createColorPalette(teamColor);
  const legend = getLegend(lineupBins, colorPalette);
  const seriesFormatter = colorFormatter(
    lineupBins,
    colorPalette,
    playerSelected,
  );

  const options = {
    legend: {
      show: true,
    },
    grid: {
      left: 40,
      bottom: 10,
      right: 10,
      top: 10,
    },
    tooltip: {
      ...tooltipOptions,
      formatter: LineupBarChartTooltip,
      triggerOn: 'mousemove',
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: maxValue,
      minInterval: 0,
      maxInterval: 2,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: GreyColors.splitLine },
    },
    yAxis: {
      data: ['Lineups'],
      show: false,
    },
    series: [
      {
        type: 'custom',
        renderItem: renderItem,
        itemStyle: {
          color: seriesFormatter,
          opacity: 0.8,
        },
        encode: {
          x: [1, 2],
          y: 0,
        },
        data: chartData,
      },
    ],
  };

  return { options, legend, players };
}

function getLegend(lineupBins, colorPalette) {
  const colors = chroma.scale(colorPalette).colors(lineupBins.length);

  return lineupBins.map((bin, index) => ({
    points: bin.x1,
    color: colors[index],
  }));
}

function renderItem(params, api) {
  var startRect = api.coord([api.value(1), 0]);
  var endRect = api.coord([api.value(2), 0]);
  var heightRect = api.size([0, 1])[1] * 0.6;

  var widthLine = 2;
  var startLine = api.coord([api.value(1), 0]);
  var heightLine = api.size([0, 1])[1] * 0.9;

  var rectShape = echarts.graphic.clipRectByRect(
    {
      x: startRect[0],
      y: startRect[1] - heightRect / 2,
      width: endRect[0] - startRect[0],
      height: heightRect,
    },
    {
      x: params.coordSys.x,
      y: params.coordSys.y,
      width: params.coordSys.width,
      height: params.coordSys.height,
    },
  );

  return {
    type: 'group',
    children: [
      {
        type: 'rect',
        shape: rectShape,
        style: api.style(),
      },
      {
        type: 'rect',
        shape: {
          x: startLine[0],
          y: startLine[1] - heightLine / 2,
          width: widthLine,
          height: heightLine,
        },
        style: api.style({ fill: '#B4B4B4' }),
      },
    ],
  };
}

function getChartData(shifts, teamColor) {
  return shifts.map(teamShift => ({
    value: [
      0,
      teamShift.startShiftTime,
      teamShift.endShiftTime,
      teamShift.boxscore.pts,
    ],
    teamShift,
    teamColor,
  }));
}

function colorFormatter(lineupBins, colors, playerSelected) {
  return ({ value, data }) => {
    if (!playerSelected) {
      return getRegularColors(lineupBins, colors, value);
    } else {
      return getHighlightColors(
        lineupBins,
        colors,
        value,
        data,
        playerSelected,
      );
    }
  };
}

function getRegularColors(lineupBins, colors, value) {
  const valueBin = lineupBins.find(bin => bin.indexOf(value[3]) !== -1);
  const colorIndex = lineupBins.indexOf(valueBin) || 0;

  return chroma.scale(colors).colors(lineupBins.length)[colorIndex];
}

function getHighlightColors(lineupBins, colors, value, data, playerSelected) {
  const greyColor = '#4a4a4a';
  const regularColor = getRegularColors(lineupBins, colors, value);

  return hasSelectedPlayer(data.teamShift.lineup, playerSelected)
    ? regularColor
    : greyColor;
}

function hasSelectedPlayer(lineup, playerSelected) {
  return lineup.players.some(player => player.id === playerSelected);
}

function createColorPalette(color) {
  const lighterColor = chroma(color)
    .desaturate(1.5)
    .brighten(1.5);

  return [lighterColor, color];
}

function getLineupBins(chartData) {
  const seriesData = chartData.map(data => data.value[3]);

  return getBins(seriesData, BINS_NUMBER);
}

function getTeamPlayers(shifts) {
  const players = {};

  shifts.forEach(shift =>
    shift.lineup.players.forEach(player => {
      const shiftPlusMinus = shift.boxscore.plus_minus;

      if (player) {
        players[player.id] = players[player.id] || {
          ...player,
          plusMinusPlayer: 0,
        };
        players[player.id].plusMinusPlayer += shiftPlusMinus;
      }

      return players;
    }),
  );

  return Object.keys(players).map(playerId => players[playerId]);
}
